/**
 * Compact "Inventory for this room" panel shown under a room's tasks in the cleaner flow.
 * Reuses the same update/request/purchase patterns as InventoryCheckTab.
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, ShoppingCart, CheckCircle2 } from "lucide-react";
import { FULLNESS_OPTIONS, getFullnessLabel } from "@/hooks/useInventory";
import { getInStockCount } from "@/lib/inventoryQuantity";
import { toast } from "@/hooks/use-toast";

export interface RoomInventoryItem {
  id: string;
  item_name: string;
  in_stock_qty?: number | null;
  ordered_qty?: number | null;
  current_count?: number | null;
  threshold_count: number | null;
  quantity_type?: string | null;
  current_value?: number | null;
  threshold_value?: number | null;
}

const getItemStatus = (item: RoomInventoryItem) => {
  if (item.quantity_type === "fullness") {
    const c = item.current_value ?? 0;
    const t = item.threshold_value ?? 0.25;
    if (c === 0) return "out";
    if (c <= t) return "low";
    return "ok";
  }
  const c = getInStockCount(item);
  const t = item.threshold_count;
  if (t == null) return "ok";
  if (c === 0) return "out";
  if (c <= t) return "low";
  return "ok";
};

interface RoomInventoryPanelProps {
  roomName: string;
  items: RoomInventoryItem[];
  readOnly: boolean;
  tDynamic: (text: string) => string;
  onUpdateItem: (params: {
    item_id: string;
    item_name: string;
    count?: number;
    fullness_value?: number;
  }) => Promise<void>;
  onRequestOrder: (item: RoomInventoryItem) => void;
  onPurchased: (item: RoomInventoryItem) => void;
  /** When true for an item id, show "Request made" instead of opening another request. */
  hasRequestOrderForItem?: (itemId: string) => boolean;
}

export function RoomInventoryPanel({
  roomName,
  items,
  readOnly,
  tDynamic,
  onUpdateItem,
  onRequestOrder,
  onPurchased,
  hasRequestOrderForItem,
}: RoomInventoryPanelProps) {
  const hasReq = hasRequestOrderForItem ?? (() => false);
  const [fullnessValues, setFullnessValues] = useState<Record<string, number>>({});
  const [counts, setCounts] = useState<{ item_id: string; count: number }[]>([]);

  useEffect(() => {
    const initial: Record<string, number> = {};
    const countList: { item_id: string; count: number }[] = [];
    items.forEach((item) => {
      if (item.quantity_type === "fullness") {
        initial[item.id] = item.current_value ?? 1.0;
      } else {
        countList.push({ item_id: item.id, count: getInStockCount(item) });
      }
    });
    setFullnessValues(initial);
    setCounts(countList);
  }, [items]);

  const handleFullnessChange = async (item: RoomInventoryItem, value: number) => {
    setFullnessValues((prev) => ({ ...prev, [item.id]: value }));
    try {
      await onUpdateItem({
        item_id: item.id,
        item_name: item.item_name,
        count: 0,
        fullness_value: value,
      });
      toast({
        title: tDynamic("Updated"),
        description: `${item.item_name}: ${getFullnessLabel(value)}`,
      });
    } catch {
      toast({
        title: tDynamic("Error"),
        description: tDynamic("Failed to update"),
        variant: "destructive",
      });
    }
  };

  const updateCountLocal = (item_id: string, count: number) => {
    setCounts((prev) =>
      prev.some((c) => c.item_id === item_id)
        ? prev.map((c) => (c.item_id === item_id ? { ...c, count } : c))
        : [...prev, { item_id, count }]
    );
  };

  const handleCountBlur = async (item: RoomInventoryItem) => {
    const c = counts.find((x) => x.item_id === item.id);
    const value = c?.count ?? getInStockCount(item);
    try {
      await onUpdateItem({
        item_id: item.id,
        item_name: item.item_name,
        count: Math.max(0, value),
      });
      toast({ title: tDynamic("Updated"), description: item.item_name });
    } catch {
      toast({
        title: tDynamic("Error"),
        description: tDynamic("Failed to update"),
        variant: "destructive",
      });
    }
  };

  if (!items || items.length === 0) return null;

  return (
    <div className="rounded-lg border bg-muted/30 p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Package className="w-4 h-4 text-muted-foreground" />
        <span className="text-sm font-medium">{tDynamic("Inventory for this room")}</span>
        <span className="text-xs text-muted-foreground">({roomName})</span>
      </div>
      <ul className="space-y-3">
        {items.map((item) => {
          const status = getItemStatus(item);
          const isFullness = item.quantity_type === "fullness";
          const currentFullness = fullnessValues[item.id] ?? item.current_value ?? 1.0;
          const countState = counts.find((c) => c.item_id === item.id);
          const currentCount = countState?.count ?? getInStockCount(item);

          return (
            <li key={item.id} className="border-b border-border/60 pb-3 last:border-0 last:pb-0">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{tDynamic(item.item_name)}</p>
                  {isFullness ? (
                    <p className="text-xs text-muted-foreground">
                      {tDynamic("Fullness")}: {getFullnessLabel(item.current_value ?? 1.0)}
                      {item.threshold_value != null && (
                        <> · {tDynamic("Threshold")}: {getFullnessLabel(item.threshold_value)}</>
                      )}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      {tDynamic("In stock")}: {currentCount}
                      {item.threshold_count != null && (
                        <> · {tDynamic("Threshold")}: {item.threshold_count}</>
                      )}
                    </p>
                  )}
                </div>
                {status !== "ok" && (
                  <span className="text-xs font-medium text-amber-600 shrink-0">
                    {status === "out" ? tDynamic("Out") : tDynamic("Low")}
                  </span>
                )}
              </div>
              {!readOnly && (
                <div className="mt-2 space-y-2">
                  {isFullness ? (
                    <div className="flex flex-wrap gap-1.5">
                      {FULLNESS_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => handleFullnessChange(item, opt.value)}
                          className={`min-h-8 px-2.5 rounded-md text-xs font-medium ${
                            currentFullness === opt.value
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted hover:bg-muted/80"
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-muted-foreground shrink-0">
                        {tDynamic("Count")}:
                      </Label>
                      <Input
                        type="number"
                        min={0}
                        value={currentCount}
                        onChange={(e) => {
                          const raw = e.target.value;
                          const n = raw === "" ? 0 : parseInt(raw, 10);
                          if (!Number.isNaN(n)) updateCountLocal(item.id, Math.max(0, n));
                        }}
                        onBlur={() => handleCountBlur(item)}
                        className="h-8 w-20 text-center text-sm"
                      />
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {hasReq(item.id) ? (
                      <div className="inline-flex h-8 items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 text-xs font-medium text-emerald-800 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-100">
                        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {tDynamic("Request made")}
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => onRequestOrder(item)}
                      >
                        <ShoppingCart className="w-3 h-3 mr-1" />
                        {tDynamic("Request owner to order")}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={() => onPurchased(item)}
                    >
                      {tDynamic("I purchased this myself")}
                    </Button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
